import { FC } from 'react';
import cx from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import { TEAM_INJURY_METRIC } from '../../../enums/injuries';
import { ITeamTimelineMetric } from '../../../models/injury';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { ChartEmptyState } from '../AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { CustomBadge } from '../../../shared/CustomBadge/CustomBadge';
import styles from './TeamInjuryTimeline.module.scss';

interface Props {
  teamId: string;
  period?: string;
}

// NOTE: usa il metric `timeline` (non getTeamInjuries) perché ha firstName/lastName/injuryType/status

export const TeamInjuryTimeline: FC<Props> = ({ teamId, period }) => {
  const endpoints = useInjuryEndpoints();
  const { data, isLoading, isError, refetch } = useQuery<ITeamTimelineMetric>({
    queryKey: ['injury', 'team', teamId, 'metric', TEAM_INJURY_METRIC.TIMELINE, period ?? 'all'],
    queryFn: async () => {
      const { data } = await endpoints.getTeamInjuryMetric(teamId, TEAM_INJURY_METRIC.TIMELINE, period ? { period } : undefined);
      return data as ITeamTimelineMetric;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) return (
    <div className={styles.loadingState}>
      <LeopartLoader size="sm" />
    </div>
  );
  if (isError) return <ChartErrorState onRetry={refetch} message="Impossibile caricare lo storico team" />;
  if (!data?.data?.length) return <ChartEmptyState />;

  const sorted = [...data.data].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>Storico infortuni team</h3>
      <div className={styles.list}>
        {sorted.map((inj) => {
          const isActive = inj.status === 'ACTIVE';
          return (
            <div key={inj.injuryId} className={styles.row}>
              <div className={cx(styles.bar, isActive ? styles.barActive : styles.barClosed)} />
              <div className={styles.rowBody}>
                <div className={styles.rowHead}>
                  <span className={styles.athleteName}>{inj.firstName} {inj.lastName}</span>
                  <span className={styles.separator}>·</span>
                  <span className={styles.meta}>{inj.bodyZone} · {inj.injuryType}</span>
                  <CustomBadge
                    className={styles.statusBadge}
                    color={isActive ? 'lightred' : 'lightgreen'}
                    label={isActive ? `Attivo · ${inj.daysLost ?? '–'} gg` : `Chiuso · ${inj.daysLost ?? '–'} gg`}
                  />
                </div>
                <div className={styles.rowFoot}>
                  {new Date(inj.date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {inj.isRecurrence && (
                    <span className={styles.recurrence}>Recidiva</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
